import { t } from 'i18n';
import React, { ReactNode, useEffect, useState } from 'react';
import { Footer, CustomLink } from '@teko/ecommerce';
import { styled, LazyImage } from '@teko/ui';
import { NavigationBar } from './components/NavigationBar';
import { JSONObject } from 'types';
import { useTenantConfig } from 'hooks';
import { ForceLogin } from 'components/ForceLogin';
import { Topbar, TopbarButton } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { LazyComponent } from 'components/LazyComponent';
import { StringParam, useQuery } from 'hooks/useQuery';
import dynamic from 'next/dynamic';
import { BannerInlinePlaceholder } from '@tekface/smart-activation';
import { INLINE_BANNER_CLASSNAME } from '../../../../constants';
import { FloatingIconList } from './components/FloatingIconList';

const PbLayout = dynamic(() =>
  import('modules/page-builder/layout').then((mod) => mod.PbLayout),
);

const TopBannerHeight = styled.div`
  height: 56px;
  @media print {
    display: none;
  }
`;

interface CustomLayoutProps extends LayoutProps {
  leftMenu?: Array<ReactNode>;
  rightMenu?: Array<ReactNode>;
  footer?: React.ReactNode;
  navigationBar?: React.ReactNode;
  forceLogin?: boolean;
}
const DefaultLayout: React.FC<CustomLayoutProps> = ({
  children,
  banners,
  leftMenu,
  rightMenu,
  footerData,
  footer,
  navigationBar,
  forceLogin = false,
  layoutConfig,
  pageProps,
  inlineBanners,
}: CustomLayoutProps) => {
  const LoginWrapper = forceLogin ? ForceLogin : React.Fragment;
  const { partner, footerDataConfig, noOverride, epicPb1887 } =
    useTenantConfig();
  const topBanner = banners?.[0];

  if (epicPb1887) {
    return (
      <PbLayout config={layoutConfig || {}} pageProps={pageProps}>
        <LoginWrapper>
          {children}
          <FloatingIconList />
        </LoginWrapper>
      </PbLayout>
    );
  }

  if ((!footer && footerData) || noOverride) {
    const footerLayout = noOverride
      ? {
          ...footerData,
          supportAndPolicy: [
            ...(footerDataConfig?.['supportAndPolicy' as keyof JSONObject] ??
              []),
          ],
        }
      : footerData;
    footer = <Footer footer={footerLayout ?? {}} />;
  }
  const renderContent = () => {
    // Render custom pages
    // if (allowRenderCustomPage) {
    //   return <>{children}</>;
    // }

    return (
      <>
        {!partner && (
          <>
            {topBanner && (
              <TopBannerHeight
                {...getTrackingData({
                  regionName: 'topBanner',
                  isTrackContent: true,
                })}
              >
                <CustomLink targetPath={topBanner.targetPath}>
                  <LazyImage
                    imageWidth={1920}
                    height={56}
                    cover
                    src={topBanner.image}
                  />
                </CustomLink>
              </TopBannerHeight>
            )}
            <BannerInlinePlaceholder
              banners={inlineBanners}
              className={INLINE_BANNER_CLASSNAME.INLINE_HOME_TOP}
            />
            {(leftMenu || rightMenu) && (
              <Topbar leftMenu={leftMenu} rightMenu={rightMenu} />
            )}
          </>
        )}
        {navigationBar}
        {<div>{children}</div>}
        <LazyComponent>{footer}</LazyComponent>
      </>
    );
  };

  return (
    <>
      <LoginWrapper>{renderContent()}</LoginWrapper>
      <FloatingIconList />
    </>
  );
};
export const Layout: React.FunctionComponent<CustomLayoutProps> = ({
  children,
  banners,
  leftMenu,
  rightMenu,
  footerData,
  footer,
  navigationBar,
  menuItems,
  liteLogo,
  forceLogin = false,
  layoutAreas,
  ...props
}) => {
  const { identityRequired, hasNews } = useTenantConfig();
  const [requireLogin, setRequireLogin] = useState(forceLogin);
  const finalRightActions: Array<ReactNode> = hasNews
    ? [
        <TopbarButton key={0} href="/tin-tuc">
          {t('Tin tức')}
        </TopbarButton>,
        ...(rightMenu ?? []),
      ]
    : rightMenu ?? [];
  const query = useQuery({ ref_code: StringParam });

  useEffect(() => {
    if (identityRequired && query?.ref_code) {
      sessionStorage.setItem('ref_code', query.ref_code.toString());
      setRequireLogin(true);
    } else if (!!sessionStorage.getItem('ref_code')) {
      // for case redirected back after login
      setRequireLogin(true);
    }
  }, []);

  if (!navigationBar) {
    navigationBar = (
      <NavigationBar
        menuItems={menuItems}
        showShippingFeeMiniCart={false}
        liteLogo={liteLogo}
      />
    );
  }
  return (
    <DefaultLayout
      leftMenu={leftMenu}
      rightMenu={finalRightActions}
      navigationBar={navigationBar}
      footer={footer}
      footerData={footerData}
      banners={banners}
      forceLogin={requireLogin}
      layoutAreas={layoutAreas}
      {...props}
    >
      {children}
    </DefaultLayout>
  );
};
